import { observer } from 'mobx-react-lite';
import { useComputed } from 'owa-react-hooks/lib/useComputed';
import { AgendaEventViewController } from './AgendaEventViewController';
import { AgendaListDateMessage } from './AgendaListDateMessage';
import { DateRangeFooter } from './DateRangeFooter';
import { getScenarioActions } from '../actions/getScenarioActions';
import {
    AgendaItemRow,
    getAgendaListItems,
    getListDate,
    isAgendaItem,
    isAddTaskDateItem,
} from '../selectors/agendaEventsListViewSelectors';
import { getViewConfig, shouldShowTasks, getSelectedDate } from '../selectors/agendaStoreSelectors';
import { isOwaDate, isEmptyDateMessage, isEmptyDateHeader } from '../utils/buildListWithDates';
import { FocusZone, FocusZoneDirection, IFocusZone } from '@fluentui/react/lib/FocusZone';
import { AgendaEventsLoadingListView } from 'owa-agenda-loading-events';
import { lazyPrefetchInsights } from 'owa-calendar-related-content-initialization';
import { LoadMoreScrollList } from 'owa-load-more-scroll-list';
import * as React from 'react';
import { getTodoCache } from '../store/todoCache';
import {
    getLoadState,
    getCanLoadMore,
    getLoadedDateRange,
    getFailureState,
} from '../selectors/listEventsCalendarCacheSelectors';
import {
    InsightsDataFetchSource,
    isInsightsEnabledForUser,
    shouldPrefetchInsights,
} from 'owa-meeting-insights-types-and-flights';
import { getCalendarEventWithId } from 'owa-calendar-events-store';
import {
    HeaderFooterMessage,
    getEmptyStateProps,
    getErrorEmptyStateProps,
    EventsEmptyState,
    AgendaItem,
    AgendaListDateHeader,
} from 'owa-agenda-events-common';
import { getErrorMessageAndRefreshSuggestion } from 'owa-calendar-load-error-message';
import { isTodoType } from 'owa-todo-types';
import { assertNever } from 'owa-assert';
import { AgendaTodoListItem } from './AgendaTodoListItem';
import AgendaTodoContextMenu from './AgendaTodoContextMenu';
import { getTodoContentextMenuState } from '../selectors/agendaStoreTodoViewSelectors';
import { AgendaTodoQuickAdd } from './AgendaTodoQuickAdd';
import type { AgendaCallbacks } from '../store/schema/AgendaCallbacks';
import { AgendaListInteraction } from '../store/schema/AgendaListPayload';
import classNames from 'classnames';

export interface AgendaEventsListViewProps {
    scenarioId: string;
    callbacks?: AgendaCallbacks;
    styles?: { listContainer: string };
}
import styles from './AgendaEventsListView.scss';
type ListState = 'Shimmer' | 'Blank' | 'List';

export const AgendaEventsListView = observer(function AgendaEventsListView(
    props: AgendaEventsListViewProps
) {
    React.useEffect(() => {
        const { scenarioId } = props;
        const { updateSelectedCalendarEvent } = getScenarioActions(scenarioId);
        updateSelectedCalendarEvent(null);
        tryPrefetchMeetingInsights();
        return () => {
            const { scenarioId } = props;
            if (shouldShowTasks(scenarioId)) {
                getTodoCache(scenarioId).releaseLock();
            }
        };
    }, []);
    const focusZoneRef = React.useRef<React.RefObject<IFocusZone>>(React.createRef());
    const agendaListItems = useComputed((): AgendaItemRow[] => {
        const { scenarioId } = props;
        return getAgendaListItems(scenarioId, getSelectedDate(scenarioId));
    });
    const canLoadMore = (): boolean => {
        const { scenarioId } = props;
        const viewConfig = getViewConfig(scenarioId);
        return getCanLoadMore(viewConfig.maxDays, scenarioId);
    };
    const getList = (): JSX.Element => {
        switch (getListState()) {
            case 'List':
                const { scenarioId } = props;
                const viewConfig = getViewConfig(scenarioId);
                const loadState = getLoadState(scenarioId);
                const dateRange = getLoadedDateRange(scenarioId);
                const contextMenuState = getTodoContentextMenuState(scenarioId);
                const agendaItems = agendaListItems.get();
                const listProps = {
                    items: agendaItems,
                    onRenderCell: renderAgendaListItem,
                    renderedWindowsAhead: 3,
                    renderedWindowsBehind: 3,
                    onListScrollWithDelay: onListScrollWithDelay,
                    onListScroll: onListScroll,
                };
                const hasEvents = agendaItems.length !== 0;
                const showFooter = viewConfig.showDateRangeFooter && !canLoadMore();
                const overallFailureState = getFailureState(scenarioId);
                const hasFailure = overallFailureState !== null;
                // Show the partial error header when we are able to load some events, but there were failures.
                const showPartialErrorHeader = hasFailure && hasEvents;
                // Show the empty state when we have no events, and we cannot load any more.
                const isEmptyState = !hasEvents && !canLoadMore();
                return (
                    <FocusZone
                        className={classNames(
                            styles.eventsListViewZone,
                            props.styles?.listContainer
                        )}
                        direction={FocusZoneDirection.vertical}
                        componentRef={focusZoneRef.current}>
                        {isEmptyState ? (
                            <div className={styles.agendaEmptyState}>
                                <EventsEmptyState
                                    {...(hasFailure
                                        ? getErrorEmptyStateProps(overallFailureState)
                                        : getEmptyStateProps(dateRange))}
                                />
                            </div>
                        ) : (
                            <>
                                <LoadMoreScrollList
                                    scenarioId={props.scenarioId}
                                    listProps={listProps}
                                    isLoadRowsInProgress={loadState === 'Fetching'}
                                    dataSourceId={'Agenda_List'}
                                    currentLoadedIndex={agendaItems.length}
                                    className={styles.list}
                                    onLoadMoreRows={onLoadMoreItems}
                                    getCanLoadMore={canLoadMore}
                                    postRowsComponent={
                                        showFooter && (
                                            <DateRangeFooter
                                                scenarioId={props.scenarioId}
                                                dateRange={dateRange}
                                            />
                                        )
                                    }
                                    preRowsComponent={
                                        showPartialErrorHeader && (
                                            <HeaderFooterMessage
                                                message={getErrorMessageAndRefreshSuggestion(
                                                    overallFailureState
                                                )}
                                            />
                                        )
                                    }
                                />
                                {contextMenuState && (
                                    <AgendaTodoContextMenu
                                        scenarioId={props.scenarioId}
                                        contextMenuState={contextMenuState}
                                    />
                                )}
                            </>
                        )}
                    </FocusZone>
                );
            default:
                return null;
        }
    };
    const getListState = (): ListState => {
        const { scenarioId } = props;
        const loadState = getLoadState(scenarioId);
        const agendaItems = agendaListItems.get();

        const shouldShowBlank =
            (loadState === 'Loading' || loadState === 'DoesNotExist') && agendaItems.length === 0;
        const shouldShowShimmer = loadState === 'Fetching' && agendaItems.length === 0;

        if (shouldShowBlank) {
            return 'Blank';
        } else if (shouldShowShimmer) {
            return 'Shimmer';
        } else {
            return 'List';
        }
    };
    const renderCalendarEvent = (agendaItem: AgendaItem): JSX.Element => {
        const { scenarioId, callbacks } = props;
        return (
            <AgendaEventViewController
                key={agendaItem.id.Id}
                agendaItem={agendaItem}
                scenarioId={scenarioId}
                listRef={focusZoneRef.current}
                callback={callbacks?.calendarEventInteractionCallback}
            />
        );
    };
    const onListScrollWithDelay = (topOfListIndex: number) => {
        const { scenarioId, callbacks } = props;
        const { updateTopOfListIndex } = getScenarioActions(scenarioId);
        updateTopOfListIndex(topOfListIndex);
        const date = getListDate(scenarioId, agendaListItems.get());
        callbacks?.listInteractionCallback?.({
            type: AgendaListInteraction.OnDateUpdated,
            date: date,
        });
    };
    const onListScroll = (isAutoScroll: boolean) => {
        if (!isAutoScroll) {
            // if the user scrolls the view, we no longer want to scroll them to the up next
            const { scenarioId } = props;
            const { updateShouldAutoScroll } = getScenarioActions(scenarioId);
            updateShouldAutoScroll(false);
        }
    };
    const onLoadMoreItems = () => {
        const { scenarioId } = props;
        const { onLoadMoreItems } = getScenarioActions(scenarioId);
        onLoadMoreItems();
    };
    const renderAgendaListItem = (item: AgendaItemRow): JSX.Element => {
        if (isOwaDate(item)) {
            return <AgendaListDateHeader date={item} />;
        } else if (isEmptyDateHeader(item)) {
            return <AgendaListDateHeader date={item.date} />;
        } else if (isEmptyDateMessage(item)) {
            return (
                <AgendaListDateMessage
                    text={item.text}
                    date={item.date}
                    buttonText={item.buttonText}
                    scenarioId={props.scenarioId}
                />
            );
        } else if (isTodoType(item)) {
            return <AgendaTodoListItem scenarioId={props.scenarioId} key={item.Id} todo={item} />;
        } else if (isAgendaItem(item)) {
            return renderCalendarEvent(item);
        } else if (isAddTaskDateItem(item)) {
            return <AgendaTodoQuickAdd scenarioId={props.scenarioId} date={item.date} />;
        }
        return assertNever(item as never);
    };
    const tryPrefetchMeetingInsights = (): void => {
        if (!isInsightsEnabledForUser()) {
            return;
        }

        if (!shouldPrefetchInsights()) {
            return;
        }

        const calendarAgendaItems = agendaListItems.get().filter(isAgendaItem);
        if (calendarAgendaItems.length === 0) {
            return;
        }

        const calendarEvents = calendarAgendaItems.map(agendaItem =>
            getCalendarEventWithId(agendaItem.id.Id)
        );

        lazyPrefetchInsights.importAndExecute(
            calendarEvents,
            InsightsDataFetchSource.PrefetchOnAgendaView
        );
    };
    const isDataLoaded = getListState() !== 'Shimmer';
    return (
        <AgendaEventsLoadingListView
            shimmerClassName={styles.loadingListViewWrapper}
            isDataLoaded={isDataLoaded}>
            <div className={styles.listContainer}>{getList()}</div>
        </AgendaEventsLoadingListView>
    );
});
